import { Suspense } from 'react'
import { type Metadata } from 'next'
import Link from 'next/link'
import { breadcrumbSchema } from '@entities/fulfillment'
import { JsonLd } from '@ui/JsonLd'
import {
  faqPageSchema,
  LandingAnchorNav,
  LandingBenefits,
  LandingFaq,
  LandingFigureRowPlaceholder,
  LandingFinalCall,
  LandingSteps,
  landingType,
} from '@ui/landing'

import { joinPagePath } from '../config'
import { CatalogNumbers } from './join-catalog-numbers'
import { benefits, questions, sections, steps } from './join-content'
import { JoinHero } from './join-hero'
import { JoinReview } from './join-review'

/**
 * Размер аудитории Evirma. Число округлено вниз и живёт в коде, а не приходит запросом:
 * счётчика пользователей в публичном API нет, а такая точность странице не нужна.
 *
 * На чём основано (проверять раз в несколько месяцев, расти может только вверх): 93 тысячи
 * учётных записей на сайте и 191 тысяча устройств, заходивших с расширением за последний
 * месяц. Поэтому «более 100 000» — осторожная оценка, а не преувеличение. Учётные записи и
 * устройства пересекаются, поэтому подпись всегда уточняет: считаем сайт и расширение вместе.
 */
const audience = 'более 100 000'

const title = 'Разместить фулфилмент в каталоге Evirma'
const description =
  'Продавцы маркетплейсов ищут фулфилмент в Evirma. Разместите карточку с услугами и ценами и получайте заявки — бесплатно.'

export const joinMetadata: Metadata = {
  title,
  description,
  alternates: { canonical: joinPagePath },
  openGraph: { title, description, url: joinPagePath },
}

export const FulfillmentJoinPage = () => (
  <div className="text-foreground w-full bg-white">
    <JsonLd
      data={breadcrumbSchema([
        { name: 'Все фулфилменты', path: '/fulfillment' },
        { name: 'Разместить фулфилмент', path: joinPagePath },
      ])}
    />
    <JsonLd data={faqPageSchema(questions)} />

    <LandingAnchorNav actionHref="/fulfillment/add" actionLabel="Разместить фулфилмент" sections={sections} />

    <div className={landingType.container}>
      <nav
        aria-label="Хлебные крошки"
        className={`${landingType.noteText} ${landingType.mutedText} flex flex-wrap items-center gap-2 pt-6`}
      >
        <Link href="/fulfillment" className="underline-offset-4 hover:underline">
          Все фулфилменты
        </Link>
        <span aria-hidden="true">\</span>
        <span aria-current="page">Разместить фулфилмент</span>
      </nav>

      <JoinHero audience={audience} />

      <Suspense fallback={<LandingFigureRowPlaceholder />}>
        <CatalogNumbers />
      </Suspense>

      <LandingSteps
        id="how-it-works"
        title="От карточки до первой заявки"
        intro="Три шага без договора и без платы. Заполненную карточку смотрит наша поддержка, обычно в течение дня. Дальше карточка работает сама."
        items={steps}
      />
    </div>

    <LandingBenefits
      id="benefits"
      title="Что даёт карточка"
      intro="Продавец видит цены, города и схемы работы до того, как напишет вам. Разговор начинается не со «здравствуйте», а с конкретного груза."
      items={benefits}
    />

    <div className={landingType.container}>
      <JoinReview />
      <LandingFaq id="faq" title="Частые вопросы" items={questions} />
    </div>

    <LandingFinalCall
      title="Разместите фулфилмент сегодня"
      text="Заполнение занимает несколько минут, дальше карточку проверяет поддержка. Если что-то не подойдёт, цены, услуги и контакты правятся в любой момент."
      action={{ href: '/fulfillment/add', label: 'Разместить фулфилмент' }}
      secondary={{ href: '/fulfillment', label: 'Посмотреть каталог' }}
      note="Бесплатно · без договора · правки в любой момент"
    />
  </div>
)
